<template>
	<base-layout>
		<template v-slot:header>
			Companies
		</template>
		<template v-slot:main>
      <div v-if="user">
        <div class="sm:hidden mt-4">
          <label for="tabs" class="sr-only">Select view</label>
          <select v-model="currentTab" class="form-select mt-1 block w-full">
            <option value="0">All Companies</option>
            <option value="1">Suggested By AI</option>
          </select>
        </div>
        <div class="hidden sm:block">
          <div class="border-b border-gray-200">
            <nav class="-mb-px flex space-x-8" aria-label="Tabs">
              <button type="button" class="tab-btn" @click="currentTab = '0'"
                :class="{'tab-inactive': currentTab !== '0', 'tab-active': currentTab === '0'}">
                All Companies
              </button>
              <div class="relative tab-tooltip-anchor">
                <button type="button" class="tab-btn" @click="currentTab = '1'"
                  :class="{'tab-inactive': currentTab !== '1', 'tab-active': currentTab === '1'}">
                  Suggested By AI
                </button>
                <div class="tab-tooltip absolute z-10 mt-2 rounded bg-gray-800 px-3 py-2 text-sm text-white shadow-lg">
                  These companies are recommended by AI based on your company profile and your recent broadcasts.
                </div>
              </div>
            </nav>
          </div>
        </div>
      </div>

			<div v-if="currentTab === '0'">
				<!-- Search form --> 
				<form @submit.self.prevent="doSearchAndMap">
					<div class="grid grid-cols-12 gap-6 lg:flex lg:items-end" :class="{'mt-6': user}">
						<div class="col-span-12 sm:col-span-6 lg:flex-1">
							<label for="cSearch" class="form-label block truncate">Search Companies, Contact Persons or Specialty</label>
							<input v-model="tempQuery" id="cSearch" class="form-input mt-1 block w-full" placeholder="E.g.: HP/IBM or Laptop"/>
						</div>

						<div class="col-span-12 sm:col-span-6 lg:w-64 lg:flex-shrink-0">
							<label for="cCountry" class="form-label block">Country</label>
							<select v-model="payload.country" id="cCountry" class="form-select mt-1 block w-full">
								<option v-for="item in countriesOptions" :value="item.value" :key='item.value'>{{item.name}}</option>
							</select>
						</div>

						<div class="col-span-12 lg:flex-shrink-0">
							<button @click="doSearchAndMap" type="button" class="btn btn-primary float-right lg:float-none">Search</button>
						</div>
					</div>
				</form>

				<div v-if="user" class='mt-6'>
					<div class='h-8'>
						<div class="text-red-700 text-sm float-left">
							<router-link v-if='user' to="/favorites" class="mr-2">Your Favorites ({{favoritesCount()}})</router-link>
						</div>
						<div class="text-red-700 text-sm float-right">
							<div v-if="isMapShown">
								<button @click="resetMap" class='mr-2'>Reset Map</button>
								<button @click="toggleMap">Hide Map</button>
							</div>
							<div v-else>
								<button @click="toggleMap">Show Map</button>
							</div>
						</div>
					</div>
					<div v-if='isGoogleMapsLoaded'> <!-- v-show instead of v-if so map doesnt get confused -->
						<div class='map-wrapper overflow-hidden' :class="{'map-wrapper-hidden': !isMapShown}">
							<GmapMap ref="mapRef" :center="{ lat: 20, lng: 20 }" :zoom="2" :options="mapOptions" class='mt-4' style="width: 100%; height: 100%">
								<gmap-info-window :options="infoOptions" :position="infoWindowPos" :opened="infoWinOpen" @closeclick="infoWinOpen=false"> </gmap-info-window>

								<!-- <GmapCluster> -->
									<GmapMarker
										v-for="(m, index) in markersEnriched"
										:key="index"
										:position="m.position"
										:clickable="true"
										@click="toggleInfoWindow(m,index)"
									/>
								<!-- </GmapCluster> -->
							</GmapMap>
						</div>
					</div>
				</div>
				<div v-else>
					<router-link to="/register"><img src="/img/companies_map.png" alt="Companies Map" class="mt-12 w-full h-full"></router-link>
				</div>

				<div v-if='payload.showSearch'>

					<div class='float-right hidden md:inline-block'>
						<select  v-model='pageSize' class="mt-1 mb-2 form-select pr-8">
							<option v-for='availPageSize in availPageSizes' :key='availPageSize.value' :value='availPageSize.value'>{{availPageSize.label}}</option>
						</select>
					</div>

					<div class='text-sm font-semibold leading-5 text-gray-700 mt-4 pt-3 -mb-10 clearfix'>{{numTraderText}}</div>

					<!-- Table -->
					<div class="mt-12 flex flex-col hidden md:block">
						<div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
							<div v-if='nrCompanies' class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200">
								<table class="min-w-full">
									<thead>
										<tr>
											<th class="table-cell-header pr-0 pl-3 cursor-pointer"><!-- emblem gold/trusted/diamond --></th>
											<th class="table-cell-header pl-1 hover:text-red-700 cursor-pointer" @click='sort("companyname")'>Company Name</th>
											<th class="table-cell-header hover:text-red-700 cursor-pointer" @click='sort("Country")'>Country</th>
											<th class="table-cell-header hover:text-red-700 cursor-pointer" @click='sort("profile")'>Company Description</th>
											<th class="table-cell-header hover:text-red-700 cursor-pointer text-center" @click='sort("favCount")'>Favored</th>
										</tr>
									</thead>
									<tbody v-if="user">
										<tr v-for="(item, index) in companiesEnriched" :key="index" @click='navToCompany(item.id, $event)' class="bg-white hover:bg-red-50 cursor-pointer transition duration-150 ease-in-out hover-higlight-anchor" :class='{"": index % 2}'>
											<td class="table-cell text-gray-900 pr-0 pl-3"> 
												<div v-if="item.isDiamond" class='member_diamond' title="This company is a 'Diamond Member'. Diamond members have been on www.TheBrokerSite.com for 20 or more years, we do not have any unsolved complaints on file and they offer a 1 month return to base warranty."></div>
												<div v-if="item.isGold" class='member_gold' title="This company is a 'Gold Member'. Gold members have been on www.TheBrokerSite.com for 10 or more years, we do not have any unsolved complaints on file and they offer a 1 month return to base warranty."></div>
												<div v-if="item.isTrusted" class='member_trusted' title="This company is a 'Trusted Member'. Trusted members have been on www.TheBrokerSite.com for 3 or more years, we do not have any unsolved complaints on file and they offer a 1 month return to base warranty."></div>
											</td>
											<td class="table-cell text-gray-900 pl-1"> 
												<span :title='item.name' class='inline-block font-semibold truncate hover-higlight max-w-48 align-middle'>{{item.name}}</span>
												<!-- <span v-if="item.isGold" title='Gold member' class="tag ml-2 bg-yellow-200 text-yellow-800">gold</span>
												<span v-if="item.isTrusted" title='Trusted member' class="tag ml-2 bg-gray-100 text-gray-800">trusted</span> -->
											</td>
											<td class="table-cell text-gray-500">{{item.country}}</td>
											<td class="table-cell text-gray-900"><div class="max-w-144 truncate">{{item.description}}</div></td>
											<td class="table-cell text-gray-500 text-center"><span v-if='item.favCount>0'>{{item.favCount}}</span></td>
										</tr>
									</tbody>
									<tbody v-else>
										<tr v-for="(item, index) in companiesEnriched" :key="index" class="bg-white hover:bg-red-50 cursor-pointer transition duration-150 ease-in-out hover-higlight-anchor" :class='{"": index % 2}'>
											<td class="table-cell text-gray-900 pr-0 pl-3"> 
												<div v-if="item.isDiamond" class='member_diamond' title="This company is a 'Diamond Member'. Diamond members have been on www.TheBrokerSite.com for 20 or more years, we do not have any unsolved complaints on file and they offer a 1 month return to base warranty."></div>
												<div v-if="item.isGold" class='member_gold' title="This company is a 'Gold Member'. Gold members have been on www.TheBrokerSite.com for 10 or more years, we do not have any unsolved complaints on file and they offer a 1 month return to base warranty."></div>
												<div v-if="item.isTrusted" class='member_trusted' title="This company is a 'Trusted Member'. Trusted members have been on www.TheBrokerSite.com for 3 or more years, we do not have any unsolved complaints on file and they offer a 1 month return to base warranty."></div>
											</td>
											<td class="table-cell text-gray-900 pl-1"> 
												<div class='truncate'>TBS Member</div>
											</td>
											<td class="table-cell text-gray-500">{{item.country}}</td>
											<td class="table-cell text-gray-900"><div class="max-w-144 truncate"><a href="/register" class="text-red-700">Available after registering for your Free Trial</a></div></td>
											<td class="table-cell text-gray-500 text-center">{{item.favCount}}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>

					<div class="mt-12 flex flex-col block md:hidden">
						<div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
							<div v-if='nrCompanies' class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200">
								<table class="w-full table-fixed">
									<thead>
										<tr>
											<th class="table-cell-header pr-0 pl-3 ">Companies</th>
										</tr>
									</thead>
									<tbody v-if="user">
										<tr v-for="(item, index) in companiesEnriched" :key="index" @click='navToCompany(item.id)' class="bg-white hover:bg-red-50 cursor-pointer transition duration-150 ease-in-out hover-higlight-anchor" :class='{"": index % 2}'>
											<td class="table-cell  pr-0 pl-3 "> 
												<div class='truncate'>
													<div v-if="item.isDiamond" class='float-left mr-2 member_diamond' title="This company is a 'Diamond Member'. Diamond members have been on www.TheBrokerSite.com for 20 or more years, we do not have any unsolved complaints on file and they offer a 1 month return to base warranty."></div>
													<div v-if="item.isGold" class='float-left mr-2 member_gold' title="This company is a 'Gold Member'. Gold members have been on www.TheBrokerSite.com for 10 or more years, we do not have any unsolved complaints on file and they offer a 1 month return to base warranty."></div>
													<div v-if="item.isTrusted" class='float-left mr-2 member_trusted' title="This company is a 'Trusted Member'. Trusted members have been on www.TheBrokerSite.com for 3 or more years, we do not have any unsolved complaints on file and they offer a 1 month return to base warranty."></div>
													<span class='text-red-700 font-semibold'>{{item.name}}</span>
												</div>
												<div class="truncate">{{item.description}}</div>
												<div class="truncate text-gray-500">{{item.country}}</div>
											</td>
										</tr>
									</tbody>
									<tbody v-else>
										<tr v-for="(item, index) in companiesEnriched" :key="index" class="bg-white hover:bg-red-50 cursor-pointer transition duration-150 ease-in-out hover-higlight-anchor" :class='{"": index % 2}'>
											<td class="table-cell text-gray-900 pr-0 pl-3">
												<div class='truncate'>
													<div v-if="item.isDiamond" class='float-left mr-2 member_diamond' title="This company is a 'Diamond Member'. Diamond members have been on www.TheBrokerSite.com for 20 or more years, we do not have any unsolved complaints on file and they offer a 1 month return to base warranty."></div>
													<div v-if="item.isGold" class='float-left mr-2 member_gold' title="This company is a 'Gold Member'. Gold members have been on www.TheBrokerSite.com for 10 or more years, we do not have any unsolved complaints on file and they offer a 1 month return to base warranty."></div>
													<div v-if="item.isTrusted" class='float-left mr-2 member_trusted' title="This company is a 'Trusted Member'. Trusted members have been on www.TheBrokerSite.com for 3 or more years, we do not have any unsolved complaints on file and they offer a 1 month return to base warranty."></div>
													<span class='text-red-700 font-semibold'>TBS Member</span>
												</div>
												<div class="truncate"><a href="/register" class="text-red-700">Company Description available after registering for your Free Trial</a></div>
												<div class="truncate text-gray-500">{{item.country}}</div>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>

					<Pagination :itemList="companiesEnriched" :payload="payload" :pageSize="pageSize"></Pagination>
				</div>
			</div>

      <div v-if="user && currentTab === '1'">
				<div v-if="suggestedCompanies === null" class="text-sm leading-5 text-gray-700 font-bold mt-4">Loading suggestions...</div>
				<div v-else-if="!suggestedCompanies.length" class="text-sm leading-5 text-gray-700 font-bold mt-4">No suggestions available. Please update your company details or try again later.</div>
				<template v-else>
					<div class="mt-4 flex items-center rounded-lg bg-blue-50 p-4 border border-blue-200">
						<span class="mr-3 text-2xl">&#128161;</span>
						<p class="text-sm text-blue-800">These companies are recommended by AI based on your company profile and your recent broadcasts.</p>
					</div>

					<!-- Table -->
					<div class="mt-6 flex flex-col hidden md:block">
						<div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
							<div class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200">
								<table class="min-w-full">
									<thead>
										<tr>
											<th class="table-cell-header pr-0 pl-3">Match</th>
											<th class="table-cell-header pr-0 pl-3"><!-- emblem gold/trusted/diamond --></th>
											<th class="table-cell-header pl-1">Company Name</th>
											<th class="table-cell-header">Country</th>
											<th class="table-cell-header">Company Description</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="(item, index) in suggestedCompanies" :key="index" @click='navToCompany(item.id, $event)' class="bg-white hover:bg-red-50 cursor-pointer transition duration-150 ease-in-out hover-higlight-anchor">
											<td class="table-cell text-gray-500 pr-0 pl-3">{{Math.round(item.similarity * 100)}}%</td>
											<td class="table-cell text-gray-900 pr-0 pl-3">
												<div v-if="item.isDiamond" class='member_diamond' title="This company is a 'Diamond Member'. Diamond members have been on www.TheBrokerSite.com for 20 or more years, we do not have any unsolved complaints on file and they offer a 1 month return to base warranty."></div>
												<div v-if="item.isGold" class='member_gold' title="This company is a 'Gold Member'. Gold members have been on www.TheBrokerSite.com for 10 or more years, we do not have any unsolved complaints on file and they offer a 1 month return to base warranty."></div>
												<div v-if="item.isTrusted" class='member_trusted' title="This company is a 'Trusted Member'. Trusted members have been on www.TheBrokerSite.com for 3 or more years, we do not have any unsolved complaints on file and they offer a 1 month return to base warranty."></div>
											</td>
											<td class="table-cell text-gray-900 pl-1">
												<span :title='item.name' class='inline-block font-semibold truncate hover-higlight max-w-48 align-middle'>{{item.name}}</span>
											</td>
											<td class="table-cell text-gray-500">{{item.country}}</td>
											<td class="table-cell text-gray-900"><div class="max-w-144 truncate">{{item.description}}</div></td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>

					<div class="mt-6 flex flex-col block md:hidden">
						<div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
							<div class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200">
								<table class="w-full table-fixed">
									<thead>
										<tr>
											<th class="table-cell-header pr-0 pl-3">Companies</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="(item, index) in suggestedCompanies" :key="index" @click='navToCompany(item.id, $event)' class="bg-white hover:bg-red-50 cursor-pointer transition duration-150 ease-in-out hover-higlight-anchor">
											<td class="table-cell pr-0 pl-3">
												<div class='truncate'>
													<div v-if="item.isDiamond" class='float-left mr-2 member_diamond' title="This company is a 'Diamond Member'. Diamond members have been on www.TheBrokerSite.com for 20 or more years, we do not have any unsolved complaints on file and they offer a 1 month return to base warranty."></div>
													<div v-if="item.isGold" class='float-left mr-2 member_gold' title="This company is a 'Gold Member'. Gold members have been on www.TheBrokerSite.com for 10 or more years, we do not have any unsolved complaints on file and they offer a 1 month return to base warranty."></div>
													<div v-if="item.isTrusted" class='float-left mr-2 member_trusted' title="This company is a 'Trusted Member'. Trusted members have been on www.TheBrokerSite.com for 3 or more years, we do not have any unsolved complaints on file and they offer a 1 month return to base warranty."></div>
													<span class='text-red-700 font-semibold'>{{item.name}}</span>
												</div>
												<div class="mt-1 mb-1 text-xs text-gray-500">{{Math.round(item.similarity * 100)}}% match</div>
												<div class="truncate">{{item.description}}</div>
												<div class="truncate text-gray-500">{{item.country}}</div>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</template>
      </div>
		</template>
	</base-layout>
</template>

<script>
// import { gmapApi } from 'vue2-google-maps'
import Vue from 'vue'
import BaseLayout from '@/components/BaseLayout.vue'
import ListMixin from '@/components/mixins/List'
import { mapActions, mapState, mapGetters } from 'vuex';
import { useSchemaOrg } from '../composables/useSchema.org.js';

export default {
	mixins: [ListMixin],
  	name: 'Companies',
  	components: {
    	BaseLayout,
  	},
  	data: () => ({
		isGoogleMapsLoaded: false,
		google: null,
		payload: {
			query: null, 
			country: "",
			page: 1,
			orderCol: "",
			orderDesc: false,
			showSearch: false,
		},
		fetchActionName: "getCompanies",
		numResultsName: "nrCompanies", //used in list-mixin
		mapOptions: {
			zoomControl: true,
			mapTypeControl: false,
			scaleControl: false,
			streetViewControl: false,
			rotateControl: false,
			fullscreenControl: true,
			disableDefaultUI: false
		},
		isMapShown: false,

		// Infowindow stuff
		infoWindowPos: null,
		infoWinOpen: false,
		currentMidx: null,

		infoOptions: {
		content: '',
			//optional: offset infowindow so it visually sits nicely on top of our marker
			pixelOffset: {
				width: 0,
				height: -35
			}
		},
    currentTab: "0",
		suggestedCompanies: null,
  	}),
	// Hack to load google maps
	beforeCreate: function () {


	},
	created(){
		if(window.GmapVue){ //on subsequent loads
			this.isGoogleMapsLoaded = true
		}
	},
  async mounted () {
    if (this.user) await this.fetchSuggested()
  },
  computed: {
  	...mapState([ 'nrCompanies', 'nrTraders', 'nrCountries',]),
		...mapGetters(['companiesEnriched', 'countriesOptions', 'markersEnriched']),
		numTraderText(){
			if(!this.isBootstrapped || this.isLoading) return "Loading..."
			if(!this.nrCompanies) return ``
			if(!this.payload.country) return `${this.nrTraders} ${this.nrTraders !== 1 ? "traders" : "trader"} from ${this.nrCountries} ${this.nrCountries !== 1 ? "countries" : "country"}  found`
			return `${this.nrTraders} ${this.nrTraders !== 1 ? "traders" : "trader"} from ${this.nrCompanies} ${this.nrCompanies !== 1 ? "companies" : "company"} found`
		},
  },
  methods: {
  	...mapActions(["getCompanies", "getCountryCoords"]),
  	doSearchAndMap(){
  		this.payload.showSearch = true;
  		this.doSearch()
  		this.loadMap();
  		this.isMapShown = false; //animate away map on explicit search. (this doesn't happen on page load which may already include a search)
  	},
  	async loadMap(countryName = null){
  		const country = countryName || this.payload.country;
  		if(!country){
  			this.resetMap();
  			this.isMapShown = true;
  		} else{

  			// this.isMapShown = false;
			const countryCoords = await this.getCountryCoords(country)
			if(this.mapUnderlying) {
				this.mapUnderlying.setCenter(countryCoords.payload.location);
				const bounds = new this.google.maps.LatLngBounds(countryCoords.payload.viewport.southwest,countryCoords.payload.viewport.northeast)
				this.mapUnderlying.fitBounds(bounds);
			}
				
  		}
  	},
    async fetchSuggested() {
      try{
        const { data } = await this.$http.get(`/api/companies/suggested/${this.user.companyId}`)
        this.suggestedCompanies = data?.payload?.suggestions ?? []
      } catch(err){
        this.suggestedCompanies = []
      }
    },
  	navToCompany(id){
			if(event.ctrlKey || event.metaKey) return window.open(this.$router.resolve({ name: 'companyDetail', params: { id } }).href, '_blank');
			this.$router.push({ name: 'companyDetail', params: { id } })
		},
		resetMap(){
			if(!this.isGoogleMapsLoaded) return
			this.payload.country = '';
			this.mapUnderlying.setCenter({lat: 20, lng: 20});
			this.mapUnderlying.setZoom(2);
		},
		toggleInfoWindow: function(marker, idx) {
      this.infoWindowPos = marker.position;
      this.infoOptions.content = marker.infoText;

      //check if its the same marker that was selected if yes toggle
      if (this.currentMidx == idx) {
        this.infoWinOpen = !this.infoWinOpen;
      }
      //if different marker set infowindow to open and reset current marker index
      else {
        this.infoWinOpen = true;
        this.currentMidx = idx;

      }
    },
    sort(orderCol){
		if(this.payload.orderCol === orderCol){
			this.payload.orderDesc = !this.payload.orderDesc
		} else {
			this.payload.orderCol = orderCol
		}
		this.doSearch();
	},
	favoritesCount() {
		if (this.user) {
			if (this.user.favCompanies.length===1) {
				return this.user.favCompanies[0]==0 ? this.user.favCompanies[0] : this.user.favCompanies.length
			}
			return this.user.favCompanies.length
		}
	},
	async initializeMap() {
		if (!this.user) return;
		
		const self = this;
		function dynamicallyLoadComponents(){
			self.$options.components.GmapMap = window.GmapVue.Map
			self.$options.components["gmap-info-window"] = window.GmapVue.InfoWindow
			self.$options.components.GmapMarker = window.GmapVue.Marker
			self.isGoogleMapsLoaded = true
		}

		if(window.GmapVue) {
			dynamicallyLoadComponents();
		} else {
			const gmapScript = document.createElement('script')
			gmapScript.setAttribute('src', 'https://unpkg.com/gmap-vue@1.2.2/dist/gmap-vue.js')
			gmapScript.addEventListener('load', () => {
				Vue.use(window.GmapVue, {
					load: { key: window.tbs.googleApiKey },
					installComponents: false,
				})
				dynamicallyLoadComponents();
			})
			document.head.appendChild(gmapScript)
		}

		// Wait for map to be loaded
		const i = setInterval(() => {
			if(this.isGoogleMapsLoaded){
				clearInterval(i)
				this.doRenderMarkers = false;
				this.$nextTick(() => {
					if (this.$refs.mapRef) {
						this.$refs.mapRef.$mapPromise.then(map => {
							this.mapUnderlying = map
							this.google = window.GmapVue.gmapApi()
							this.loadMap();
						})
					}
				})
			}
		}, 100)
	},

	async toggleMap() {
		this.isMapShown = !this.isMapShown;
		if (this.isMapShown && !this.isGoogleMapsLoaded) {
			await this.initializeMap();
		}

		if (this.isMapShown && this.mapUnderlying) {
			this.$nextTick(() => {
				window.google.maps.event.trigger(this.mapUnderlying, 'resize');
			});
		}
	},

	addCompanyPageSchema() {

		const schema = {
			"@context": "https://schema.org",
			"@type": "ItemList",
			"name": "IT Companies Directory - The Broker Site",
			"description": "Browse trusted IT companies, brokers, and service providers in the circular IT industry worldwide.",
			"url": "https://www.thebrokersite.com/companies",
			"numberOfItems": this.companiesEnriched.length,
			"itemListElement": this.companiesEnriched.map((company, index) => ({
				"@type": "ListItem",
				"position": index + 1,
				"item": {
					"@type": "Organization",
					"name": company.name,
					"description": company.description ? company.description.substring(0, 300) : `${company.name} - IT company based in ${company.country}`,
					"address": {
						"@type": "PostalAddress",
						"addressCountry": company.country
					},
					"location": company.lat && company.lng ? {
						"@type": "GeoCoordinates",
						"latitude": company.lat,
						"longitude": company.lng
					} : undefined,
					"url": `https://www.thebrokersite.com/companies/${company.id}`,
					"memberOf": {
						"@type": "Organization",
						"name": "The Broker Site"
					},
					...((company.isGold || company.isDiamond || company.isTrusted) && {
						"additionalProperty": [{
							"@type": "PropertyValue",
							"name": "Membership Status",
							"value": company.isDiamond ? "Diamond Member" : 
									company.isGold ? "Gold Member" : 
									"Trusted Member"
						}]
					})
				}
			})).filter(item => item.item.name) // Filter should be on item.item.name
		};
		
		useSchemaOrg(schema);
	},
  },
  watch: {
  	"payload.country"(country){
  		this.loadMap(country);
  	},
	companiesEnriched: {
      handler(newVal) {
        if (newVal) {
		  this.addCompanyPageSchema()
        }
      },
      deep: true,
      immediate: false
    },
  }
}
</script>
<style scoped>
	.member_trusted{
		width: 24px; 
		height: 24px;
		background: url("../assets/trusted_small.png");
		background-size: cover;
	}
	.member_gold{
		width: 24px; 
		height: 24px;
		background: url("../assets/gold_small.png");
		background-size: cover;
	}
	.member_diamond{
		width: 24px; 
		height: 24px;
		background: url("../assets/diamond_small.png");
		background-size: cover;
	}
	.map-wrapper{
		width: 100%; 
		height: 500px;
		transition: height 0.5s;
	}
	.map-wrapper-hidden{
		height: 0;
	}
	/* Tailwind v1's preflight outlines buttons on :focus, so a click leaves a ring behind. */
	.tab-btn:focus:not(:focus-visible){
		outline: none;
	}
	/* group-hover, w-72 and -translate-x-1/2 are all unavailable in Tailwind v1. */
	.tab-tooltip{
		display: none;
		top: 100%;
		left: 50%;
		width: 18rem;
		transform: translateX(-50%);
	}
	.tab-tooltip-anchor:hover .tab-tooltip{
		display: block;
	}
</style>
